import "./style.scss";

import { experimentalStyled as styled } from "@material-ui/core/styles";
import React, { useEffect, useRef, useState } from "react";

import ErrorBoundary from "../components/ErrorBoundary";
import useDefaults from "../hooks/useDefaults";
import useLayout from "../hooks/useLayout";
import DashboardNavbar from "./DashboardNavbar";
import DashboardSidebar from "./DashboardSidebar";

const MainLayout = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  height: "100vh",
  overflow: "hidden",
}));

const AppContent = styled("div")(() => ({
  position: "relative",
  display: "flex",
}));

const AppMain = styled("div")(({ theme }) => ({
  height: "calc(100vh - 64px)",
  width: "100%",
  padding: "20px 0 130px 0",
  overflowX: "hidden",
  overflowY: "auto",
  [theme.breakpoints.up("lg")]: {
    height: "calc(100vh - 92px)",
  },
  [theme.breakpoints.down("md")]: {
    padding: "20px 0",
  },
}));

export default function DashboardLayout({ children }) {
  useDefaults();
  // ...
  const isAuth = useLayout();
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  // ...
  useEffect(() => {
    ref?.current?.scrollTo(0, 0);
  }, [children]);
  // ...
  if (isAuth) return <>{children}</>;
  // ...
  //? Deux barrières distinctes ici, et non une seule autour du cadre entier :
  //? la barre du haut et le menu latéral sont deux zones indépendantes. Si l'une
  //? tombe, l'autre ET le contenu de la page doivent rester utilisables — c'est
  //? tout l'objet d'une barrière par zone. Voir le pavé de
  //? `components/ErrorBoundary/index.js`.
  //?
  //? ⚠️ Elles ne couvrent pas `Layout` lui-même (ses `hooks` ci-dessus, ni
  //? `AppMain`) : une barrière ne peut pas attraper le composant qui la rend. Ce
  //? cas-là est le travail de la barrière de coquille, dans `src/App.js`.
  //?
  //? `compact` : ces deux blocs remplacent des éléments de chrome, pas une page.
  //? Un bloc pleine hauteur à la place de la barre du haut décalerait toute la
  //? mise en page.
  //?
  //? Pas de `resetOn` : ce cadre ne dépend pas de la route, il n'y a donc rien
  //? dont le changement devrait désarmer ces barrières. Le bouton est la sortie.
  return (
    <MainLayout>
      <ErrorBoundary
        zone="layout-navbar"
        compact
        title="The top bar couldn't be displayed"
        message="Only this bar is affected — the menu on the left and the page below are still working. Try again to bring it back, or carry on and use the menu to move around.">
        <DashboardNavbar onOpenSidebar={() => setOpen(true)} />
      </ErrorBoundary>
      <AppContent>
        <ErrorBoundary
          zone="layout-sidebar"
          compact
          title="The menu couldn't be displayed"
          message="Navigation failed to render. The page you are on is still working and nothing has been lost. Try again to bring the menu back.">
          <DashboardSidebar
            isOpenSidebar={open}
            onCloseSidebar={() => setOpen(false)}
          />
        </ErrorBoundary>
        <AppMain ref={ref} className="scrollbar">
          {children}
        </AppMain>
      </AppContent>
    </MainLayout>
  );
}
